import React, { FunctionComponent, Suspense } from 'react'
import { colors } from '@lmc/common/constants'
import { LinkComponent } from '../../../generic'
import Field from '../../../fields'
import { getNominative } from '../../../../common/utils'
import {
  CARE_MANAGEMENT_LABEL,
  getPermissionBooleanByTemplate,
  getRoleFieldsProps,
} from './utils'
import { UserRole } from '@lmc/ts-common'

interface TeamMemberEditFormRolesPermissionsV2Props {
  features: {
    eMAR: boolean
  }
  fields: Record<string, any>
  formId: string
  preferredPeopleNominative: string
  style?: Record<string, any>
  gpConnectEnabled?: boolean
  initial?: Record<string, unknown>
  role?: UserRole
  availablePermissionsTemplates?: { id: string; label: string }[]
}

const renderEmarAdvert = () => {
  return (
    <div style={styles.emarAdvertContainer}>
      <div style={styles.emarAdvertLabel}>Medication</div>
      <LinkComponent target="_blank" to="/settings/billing">
        eMAR Feature <span className="octicon octicon-chevron-right" />
      </LinkComponent>
    </div>
  )
}

const TeamMemberEditFormRolesPermissionsV2: FunctionComponent<
  TeamMemberEditFormRolesPermissionsV2Props
> = (props) => {
  const {
    features,
    fields,
    formId,
    preferredPeopleNominative,
    style,
    gpConnectEnabled,
    initial,
    role,
    availablePermissionsTemplates,
  } = props

  const { ROLE_NOTE, ROLE_OPTIONS } = getRoleFieldsProps(
    initial,
    role,
    preferredPeopleNominative,
  )

  const hasSeniorCarerSelected = fields.role === UserRole.seniorCarer

  const getTemplateNote = () => {
    if (!fields.permissionsTemplate || fields.permissionsTemplate === null) {
      return ''
    }

    return `What’s included:  \n • Create and edit ${getNominative(
      preferredPeopleNominative,
      'Residents',
    ).toLocaleLowerCase()} profiles  \n • View and edit Logs  \n • View Team Members and re-send invites  \n • Export home data via the Download Centre`
  }

  return (
    <div style={style}>
      <div style={{ paddingBottom: 20 }}>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              className="role-field"
              formId={formId}
              keys="role"
              label="Role"
              options={ROLE_OPTIONS}
              note={ROLE_NOTE}
              required
              type="radios"
              values={fields}
            />
          </Suspense>
        </div>
        {hasSeniorCarerSelected && (
          <div style={{ marginBottom: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                values={{
                  ...fields,
                  ...(!fields?.permissionsTemplate
                    ? { permissionsTemplate: null }
                    : {}),
                }}
                formId={formId}
                keys="permissionsTemplate"
                label="Care Management"
                isRequired
                options={[
                  {
                    value: getPermissionBooleanByTemplate(
                      CARE_MANAGEMENT_LABEL,
                      availablePermissionsTemplates,
                    )?.id,
                    label: 'Yes',
                  },
                  { value: null, label: 'No' },
                ]}
                type="radios"
                note={getTemplateNote()}
              />
            </Suspense>
          </div>
        )}
        {features.eMAR ? (
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={formId}
              keys="medicationPermission"
              label="Medication"
              options={DEFAULT_RADIO_OPTIONS}
              note={getNote(preferredPeopleNominative)}
              required
              type="radios"
              values={fields}
            />
          </Suspense>
        ) : (
          renderEmarAdvert()
        )}
        {gpConnectEnabled && (
          <div style={{ marginTop: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={formId}
                keys="gpConnectPermission"
                label="GP Connect Access"
                options={DEFAULT_GP_CONNECT_OPTIONS}
                required
                type="radios"
                values={fields}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  )
}

const getNote = (preferredPeopleNominative: string) =>
  `In the Carer App: Ability to sign medication.\nIn the Care Office: Ability to manage medication for all ${getNominative(
    preferredPeopleNominative,
    'Residents',
  )}.`

const DEFAULT_RADIO_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

const DEFAULT_GP_CONNECT_OPTIONS = [
  { value: true, label: 'Clinical Access' },
  { value: false, label: 'Non-Clinical Access' },
]

const styles = {
  emarAdvertContainer: {
    display: 'flex',
  },
  emarAdvertLabel: {
    fontWeight: 600,
    marginRight: 30,
    minWidth: 160,
  },
  emarAdvertText: {
    color: colors.pink,
  },
  formField: {
    paddingBottom: 20,
  },
}

export default TeamMemberEditFormRolesPermissionsV2
